/**
 * How a human visit is named, everywhere (founder 2026-10-13: "why on
 * earth human visits are called browser visit · Chrome154?").
 *
 *   name      "Visitor from Pembroke Pines"   (city → region → country → "Visitor")
 *   subtitle  "Chrome on Windows"             (browser + OS, never a version)
 *
 * One rule for a single audit record (Mandates rows, detail header,
 * Overview feed, bundle export) and for a recognised person (ADR-0243
 * `PersonSummary`, `p.<id>` rows), so the two read identically. The
 * device labels are a closed set: no free text from the user agent ever
 * reaches the name. The raw user agent stays in Raw data, unchanged.
 *
 * Pure (no I/O, no React).
 */
/**
 * "Chrome on Mac"-grade summary of a browser user agent. Deliberately
 * coarse: a closed set of labels, never free text from the UA. (Moved
 * from the API's ADR-0243 visitor keys so the dashboard names a single
 * record exactly as the server names a person.)
 */
export function summarizeUserAgent(user_agent) {
    const ua = (user_agent ?? '').slice(0, 512);
    let browser = null;
    if (/\bEdg(?:e|A|iOS)?\//.test(ua))
        browser = 'Edge';
    else if (/\bOPR\/|\bOpera\b/.test(ua))
        browser = 'Opera';
    else if (/\bSamsungBrowser\//.test(ua))
        browser = 'Samsung Internet';
    else if (/\bFirefox\/|\bFxiOS\//.test(ua))
        browser = 'Firefox';
    else if (/\bCriOS\/|\bChrome\//.test(ua))
        browser = 'Chrome';
    else if (/\bVersion\/[\d.]+.*\bSafari\//.test(ua))
        browser = 'Safari';
    let os = null;
    if (/\biPhone\b/.test(ua))
        os = 'iPhone';
    else if (/\biPad\b/.test(ua))
        os = 'iPad';
    else if (/\bAndroid\b/.test(ua))
        os = 'Android';
    else if (/\bWindows\b/.test(ua))
        os = 'Windows';
    else if (/\bCrOS\b/.test(ua))
        os = 'ChromeOS';
    else if (/\bMacintosh\b|\bMac OS X\b/.test(ua))
        os = 'Mac';
    else if (/\bLinux\b/.test(ua))
        os = 'Linux';
    let device = 'desktop';
    if (/\biPad\b|\bTablet\b/.test(ua) || (/\bAndroid\b/.test(ua) && !/\bMobile\b/.test(ua)))
        device = 'tablet';
    else if (/\bMobi|\biPhone\b|\bAndroid\b/.test(ua))
        device = 'mobile';
    return { browser, os, device };
}
let display_names;
/**
 * "United States" for `US`. `null` for a missing / malformed code and for
 * `ZZ` (unknown): "Visitor from Unknown country" says nothing.
 */
export function visitorCountryName(code) {
    if (typeof code !== 'string' || !/^[A-Za-z]{2}$/.test(code))
        return null;
    const upper = code.toUpperCase();
    if (upper === 'ZZ')
        return null;
    if (display_names === undefined) {
        try {
            display_names = new Intl.DisplayNames(['en'], { type: 'region' });
        }
        catch {
            display_names = null;
        }
    }
    try {
        const name = display_names?.of(upper);
        return typeof name === 'string' && name.length > 0 ? name : upper;
    }
    catch {
        return upper;
    }
}
function clean(v) {
    if (typeof v !== 'string')
        return null;
    const t = v.trim();
    if (t.length === 0)
        return null;
    // Edge-resolved, but bound it so one odd value can't blow a row.
    return t.length > 60 ? `${t.slice(0, 60)}…` : t;
}
/** "Austin" → else region → else country name; `null` when nothing is known. */
export function visitorPlaceName(loc) {
    if (loc === null || loc === undefined)
        return null;
    return clean(loc.city) ?? clean(loc.region) ?? visitorCountryName(loc.country);
}
/** "Visitor from Austin" / "Visitor". */
export function humanVisitorName(loc) {
    const place = visitorPlaceName(loc);
    return place !== null ? `Visitor from ${place}` : 'Visitor';
}
/** "Chrome on Windows" / "Chrome" / "iPhone" / `null`. No versions. */
export function humanDeviceLabel(d) {
    if (d === null || d === undefined)
        return null;
    if (d.browser !== null && d.os !== null)
        return `${d.browser} on ${d.os}`;
    return d.browser ?? d.os ?? null;
}
/** A record's coarse location, or `null` when the edge resolved none. */
export function recordVisitorPlace(record) {
    const city = clean(record.geo_city);
    const region = clean(record.geo_region);
    const country = typeof record.geo_country === 'string' && record.geo_country.length > 0 ? record.geo_country : null;
    if (city === null && region === null && country === null)
        return null;
    return { city, region, country };
}
/** Is this record a human visit (the edge classed it `human_likely`)? */
export function isHumanVisit(record) {
    return record.identification_match?.visitor_class === 'human_likely';
}
/** "Visitor from Pembroke Pines" for one human record. */
export function humanRecordName(record) {
    return humanVisitorName(recordVisitorPlace(record));
}
/** "Chrome on Windows" for one human record, or `null`. */
export function humanRecordDevice(record) {
    return humanDeviceLabel(summarizeUserAgent(record.user_agent));
}
//# sourceMappingURL=human-visitor-label.js.map